import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  FileText, Plus, Search, Filter, Download, Upload, ChevronLeft, ChevronRight,
  MoreVertical, Eye, Edit, Trash2, CheckCircle, XCircle, Clock, AlertTriangle,
  Truck, Package, DollarSign, RotateCcw, Send, Loader2, AlertCircle, Check,
  CreditCard, Shield, Minus, ChevronDown
} from 'lucide-react'
import { cn, formatCurrency, formatNumber, formatDate } from '../../lib/utils'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { purchasesApi, queryKeys } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { Dropdown } from '../../components/ui/Dropdown'
import { Skeleton } from '../../components/ui/Skeleton'
import { Table } from '../../components/ui/Table'
import { Pagination } from '../../components/ui/Pagination'
import { useUIStore } from '../../stores/uiStore'
import { useAuthStore , usePermissions } from '../../stores/authStore'
import toast from 'react-hot-toast'

const statusConfig = {
  draft: { label: 'پیش‌نویس', variant: 'neutral' as const, icon: FileText },
  pending: { label: 'در انتظار تأیید', variant: 'warning' as const, icon: Clock },
  approved: { label: 'تأیید شده', variant: 'info' as const, icon: CheckCircle },
  ordered: { label: 'سفارش داده شده', variant: 'primary' as const, icon: Send },
  partial: { label: 'دریافت جزئی', variant: 'warning' as const, icon: Truck },
  completed: { label: 'تکمیل شده', variant: 'success' as const, icon: Check },
  cancelled: { label: 'لغو شده', variant: 'danger' as const, icon: XCircle },
}

export default function PurchaseOrdersPage() {
  const { hasPermission } = usePermissions()
  const { modals, openModal, closeModal } = useUIStore()
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<PurchaseOrderListParams>({
    page: 1,
    page_size: 20,
    sort_by: 'created_at',
    sort_order: 'desc',
  })
  const [deletedId, setDeletedId] = useState<string | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.purchaseOrders(filters),
    queryFn: () => purchasesApi.listPurchaseOrders(filters),
    placeholderData: (prev) => prev,
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => purchasesApi.createPurchaseOrder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders() })
      toast.success('سفارش خرید ایجاد شد')
      closeModal('create-purchase-order')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'ایجاد ناموفق بود'),
  })

  const approveMutation = useMutation({
    mutationFn: (id: string) => purchasesApi.approvePurchaseOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders() })
      toast.success('سفارش خرید تأیید شد')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'تأیید ناموفق بود'),
  })

  const orderMutation = useMutation({
    mutationFn: (id: string) => purchasesApi.orderPurchaseOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders() })
      toast.success('سفارش ارسال شد')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'ارسال ناموفق بود'),
  })

  const cancelMutation = useMutation({
    mutationFn: (id: string) => purchasesApi.cancelPurchaseOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders() })
      toast.success('سفارش خرید لغو شد')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'لغو ناموفق بود'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => purchasesApi.deletePurchaseOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders() })
      toast.success('سفارش خرید حذف شد')
      closeModal('delete-confirm')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'حذف ناموفق بود'),
  })

  const handlePageChange = (page: number) => setFilters(prev => ({ ...prev, page }))
  const handlePageSizeChange = (size: number) => setFilters(prev => ({ ...prev, page_size: size, page: 1 }))
  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); setFilters(prev => ({ ...prev, page: 1 })) }

  const totalPages = data?.total_pages || 1
  const totalItems = data?.total || 0

  const getRowDropdownItems = (po: any) => {
    const items = [
      { label: 'مشاهده', icon: Eye, onClick: () => window.open(`/purchase/orders/${po.id}`, '_blank') },
      { label: 'ویرایش', icon: Edit, onClick: () => {}, disabled: !hasPermission('purchase', 'update') || po.status !== 'draft' },
      { type: 'divider' as const },
    ]

    if (po.status === 'draft' && hasPermission('purchase', 'update')) {
      items.push({ label: 'تأیید', icon: CheckCircle, onClick: () => approveMutation.mutate(po.id) })
      items.push({ label: 'حذف', icon: Trash2, className: 'text-danger-600', onClick: () => { setDeletedId(po.id); openModal('delete-confirm') } })
    } else if (po.status === 'pending' && hasPermission('purchase', 'update')) {
      items.push({ label: 'سفارش دادن', icon: Send, onClick: () => orderMutation.mutate(po.id) })
      items.push({ label: 'لغو', icon: XCircle, className: 'text-warning-600', onClick: () => cancelMutation.mutate(po.id) })
    } else if (po.status === 'approved' && hasPermission('purchase', 'update')) {
      items.push({ label: 'سفارش دادن', icon: Send, onClick: () => orderMutation.mutate(po.id) })
      items.push({ label: 'لغو', icon: XCircle, className: 'text-warning-600', onClick: () => cancelMutation.mutate(po.id) })
    } else if (po.status === 'ordered' && hasPermission('purchase', 'update')) {
      items.push({ label: 'ثبت رسید', icon: Truck, onClick: () => window.open(`/inventory/receive?po_id=${po.id}`, '_blank') })
      items.push({ label: 'لغو', icon: XCircle, className: 'text-warning-600', onClick: () => cancelMutation.mutate(po.id) })
    } else if (po.status === 'partial' && hasPermission('purchase', 'update')) {
      items.push({ label: 'ثبت رسید', icon: Truck, onClick: () => window.open(`/inventory/receive?po_id=${po.id}`, '_blank') })
    }

    return items
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">سفارشات خرید</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">
            {totalItems.toLocaleString('fa-IR')} سفارش ثبت شده
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => openModal('create-purchase-order')} disabled={!hasPermission('purchase', 'create')}>
            <Plus className="w-4 h-4" />
            سفارش جدید
          </Button>
          <Dropdown
            trigger={<Button variant="outline"><Download className="w-4 h-4 ml-1" /> خروجی اکسل</Button>}
            items={[
              { label: 'لیست سفارشات', icon: Download, onClick: () => {} },
              { label: 'خروجی تفصیلی', icon: FileText, onClick: () => {} },
              { type: 'divider' },
              { label: 'وارد کردن سفارشات', icon: Upload, onClick: () => {} },
            ]}
          />
        </div>
      </motion.div>

      {/* Quick Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 md:grid-cols-5 gap-4"
      >
        {[
          { label: 'پیش‌نویس', count: data?.items.filter((p: any) => p.status === 'draft').length || 0, color: 'neutral', icon: FileText },
          { label: 'در انتظار', count: data?.items.filter((p: any) => p.status === 'pending').length || 0, color: 'warning', icon: Clock },
          { label: 'تأیید شده', count: data?.items.filter((p: any) => p.status === 'approved').length || 0, color: 'info', icon: CheckCircle },
          { label: 'سفارش داده شده', count: data?.items.filter((p: any) => p.status === 'ordered').length || 0, color: 'primary', icon: Send },
          { label: 'تکمیل شده', count: data?.items.filter((p: any) => p.status === 'completed').length || 0, color: 'success', icon: Check },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + i * 0.05 }}
            className="card p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">{stat.label}</p>
                <p className="text-2xl font-bold text-neutral-900 dark:text-white font-mono">{formatNumber(stat.count)}</p>
              </div>
              <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', `bg-${stat.color}-100 dark:bg-${stat.color}-900/30`)}>
                <stat.icon className={cn('w-5 h-5', `text-${stat.color}-600 dark:text-${stat.color}-400`)} />
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card p-4"
      >
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <Input placeholder="جستجو (شماره سفارش، تامین‌کننده، مرجع)..." className="flex-1 max-w-md" />
          <Select
            value={filters.status || ''}
            onChange={(v) => setFilters(prev => ({ ...prev, status: v || undefined, page: 1 }))}
            options={[
              { value: '', label: 'همه وضعیت‌ها' },
              { value: 'draft', label: 'پیش‌نویس' },
              { value: 'pending', label: 'در انتظار تأیید' },
              { value: 'approved', label: 'تأیید شده' },
              { value: 'ordered', label: 'سفارش داده شده' },
              { value: 'partial', label: 'دریافت جزئی' },
              { value: 'completed', label: 'تکمیل شده' },
              { value: 'cancelled', label: 'لغو شده' },
            ]}
            placeholder="وضعیت"
            className="w-36"
          />
          <Select
            value={filters.supplier_id || ''}
            onChange={(v) => setFilters(prev => ({ ...prev, supplier_id: v || undefined, page: 1 }))}
            options={[{ value: '', label: 'همه تامین‌کنندگان' }]}
            placeholder="تامین‌کننده"
            className="w-40"
          />
          <Select
            value={filters.warehouse_id || ''}
            onChange={(v) => setFilters(prev => ({ ...prev, warehouse_id: v || undefined, page: 1 }))}
            options={[{ value: '', label: 'همه انبارها' }]}
            placeholder="انبار مقصد"
            className="w-36"
          />
          <Dropdown
            trigger={<Button variant="outline"><Filter className="w-4 h-4 ml-1" /> فیلترهای پیشرفته</Button>}
            items={[
              { label: 'خروجی اکسل', icon: Download, onClick: () => {} },
              { type: 'divider' },
              { label: 'سفارشات معوق', icon: AlertTriangle, onClick: () => {} },
              { label: 'نیاز به تأیید', icon: Clock, onClick: () => {} },
            ]}
          />
        </form>
      </motion.div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card overflow-hidden"
      >
        {isLoading ? (
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>شماره سفارش</th>
                    <th className="hidden sm:table-cell">تاریخ</th>
                    <th className="hidden md:table-cell">تامین‌کننده</th>
                    <th className="hidden lg:table-cell">انبار مقصد</th>
                    <th>آیتم‌ها</th>
                    <th>مبلغ کل</th>
                    <th>وضعیت</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td><Skeleton variant="text" width="140px" /></td>
                      <td className="hidden sm:table-cell"><Skeleton variant="text" width="80px" /></td>
                      <td className="hidden md:table-cell"><Skeleton variant="text" width="120px" /></td>
                      <td className="hidden lg:table-cell"><Skeleton variant="text" width="100px" /></td>
                      <td><Skeleton variant="text" width="60px" /></td>
                      <td><Skeleton variant="text" width="100px" /></td>
                      <td><Skeleton variant="rectangular" width="100px" height="24px" /></td>
                      <td><Skeleton variant="rectangular" width="32px" height="32px" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <AlertTriangle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
            <p className="text-danger-600 dark:text-danger-400">خطا در بارگذاری سفارشات خرید</p>
            <Button variant="outline" onClick={() => refetch()} className="mt-4">تلاش مجدد</Button>
          </div>
        ) : data?.items.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-16 h-16 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">هیچ سفارش broni یافت نشد</h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-4">اولین سفارش خرید را ایجاد کنید</p>
            <Button onClick={() => openModal('create-purchase-order')} disabled={!hasPermission('purchase', 'create')}>
              <Plus className="w-4 h-4 ml-1" />
              ایجاد سفارش خرید
            </Button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>شماره سفارش</th>
                    <th className="hidden sm:table-cell">تاریخ</th>
                    <th className="hidden md:table-cell">تامین‌کننده</th>
                    <th className="hidden lg:table-cell">انبار مقصد</th>
                    <th>آیتم‌ها</th>
                    <th>مبلغ کل</th>
                    <th>وضعیت</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.items || []).map((po, index) => {
                    const statusInfo = statusConfig[po.status as keyof typeof statusConfig] || statusConfig.draft
                    const StatusIcon = statusInfo.icon
                    return (
                      <motion.tr
                        key={po.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.03 }}
                      >
                        <td className="font-medium text-neutral-900 dark:text-white font-mono">{po.order_number}</td>
                        <td className="hidden sm:table-cell text-neutral-600 dark:text-neutral-400 text-sm">{formatDate(po.order_date)}</td>
                        <td className="hidden md:table-cell text-neutral-700 dark:text-neutral-300">{po.supplier?.name || '—'}</td>
                        <td className="hidden lg:table-cell text-neutral-700 dark:text-neutral-300">{po.warehouse?.name || '—'}</td>
                        <td className="font-mono text-sm text-neutral-700 dark:text-neutral-300">{po.items?.length || 0}</td>
                        <td className="font-mono text-sm text-neutral-900 dark:text-white">{formatCurrency(po.total_amount)}</td>
                        <td>
                          <Badge variant={statusInfo.variant} className="text-xs flex items-center gap-1">
                            <StatusIcon className="w-3 h-3" />
                            {statusInfo.label}
                          </Badge>
                        </td>
                        <td>
                          <Dropdown
                            trigger={<button className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"><MoreVertical className="w-4 h-4" /></button>}
                            items={getRowDropdownItems(po)}
                          />
                        </td>
                      </motion.tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                نمایش {((filters.page - 1) * filters.page_size) + 1} تا {Math.min(filters.page * filters.page_size, totalItems)} از {totalItems.toLocaleString('fa-IR')}
              </div>
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                showPageSize
                pageSize={filters.page_size}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </>
        )}
      </motion.div>

      {/* Create Purchase Order Modal */}
      <Modal
        isOpen={modals['create-purchase-order']}
        onClose={() => closeModal('create-purchase-order')}
        title="ایجاد سفارش خرید جدید"
        size="xl"
      >
        <PurchaseOrderForm onSubmit={createMutation.mutate} onClose={() => closeModal('create-purchase-order')} isLoading={createMutation.isPending} />
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={modals['delete-confirm']}
        onClose={() => { closeModal('delete-confirm'); setDeletedId(null) }}
        title="حذف سفارش خرید"
        size="sm"
      >
        <p className="text-neutral-600 dark:text-neutral-400">
          آیا از حذف این سفارش خرید مطمئن هستید؟ این عملیات قابل بازگشت نیست.
        </p>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => { closeModal('delete-confirm'); setDeletedId(null) }}>انصراف</Button>
          <Button variant="danger" onClick={() => deletedId && deleteMutation.mutate(deletedId)} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? <> <Loader2 className="w-4 h-4 animate-spin" /> در حال حذف... </> : 'حذف' }
          </Button>
        </div>
      </Modal>
    </div>
  )
}

const poItemSchema = z.object({
  product_id: z.string().uuid('محصول معتبر نیست'),
  variant_id: z.string().uuid().optional().nullable(),
  quantity: z.number().min(1, 'تعداد باید حداقل ۱ باشد'),
  unit_price: z.number().min(0, 'قیمت واحد نمی‌تواند منفی باشد'),
  discount_percent: z.number().min(0).max(100).default(0),
  tax_rate: z.number().min(0).max(100).default(9),
  notes: z.string().optional(),
})

const poFormSchema = z.object({
  supplier_id: z.string().uuid('تأمین‌کننده معتبر نیست'),
  warehouse_id: z.string().uuid('انبار معتبر نیست'),
  order_date: z.string().min(1, 'تاریخ سفارش الزامی است'),
  expected_date: z.string().optional(),
  reference_number: z.string().optional(),
  notes: z.string().optional(),
  items: z.array(poItemSchema).min(1, 'حداقل یک آیتم باید وجود داشته باشد'),
})

type POFormData = z.infer<typeof poFormSchema>

interface PurchaseOrderFormProps {
  onSubmit: (data: POFormData) => void
  onClose: () => void
  isLoading: boolean
}

function PurchaseOrderForm({ onSubmit, onClose, isLoading }: PurchaseOrderFormProps) {
  const methods = useForm<POFormData>({
    resolver: zodResolver(poFormSchema),
    defaultValues: {
      supplier_id: '',
      warehouse_id: '',
      order_date: new Date().toISOString().split('T')[0],
      expected_date: '',
      reference_number: '',
      notes: '',
      items: [{ product_id: '', variant_id: null, quantity: 1, unit_price: 0, discount_percent: 0, tax_rate: 9, notes: '' }],
    },
  })

  const { control, watch } = methods
  const { fields: itemFields, append: appendItem, remove: removeItem } = useFieldArray({
    control,
    name: 'items',
  })
  const items = watch('items')

  const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0)
  const totalDiscount = items.reduce((sum, item) => sum + (item.quantity * item.unit_price * item.discount_percent / 100), 0)
  const totalTax = items.reduce((sum, item) => {
    const afterDiscount = item.quantity * item.unit_price * (1 - item.discount_percent / 100)
    return sum + (afterDiscount * item.tax_rate / 100)
  }, 0)
  const total = subtotal - totalDiscount + totalTax

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-6">
      {/* Supplier & Warehouse */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller
          name="supplier_id"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select label="تأمین‌کننده *" options={[{ value: '', label: 'انتخاب کنید' }]} placeholder="تأمین‌کننده" {...field} />
          )}
        />
        <Controller
          name="warehouse_id"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select label="انبار مقصد *" options={[{ value: '', label: 'انتخاب کنید' }]} placeholder="انبار مقصد" {...field} />
          )}
        />
        <Controller
          name="order_date"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input label="تاریخ سفارش *" type="date" {...field} />
          )}
        />
        <Controller
          name="expected_date"
          control={control}
          render={({ field }) => (
            <Input label="تاریخ انتظار دریافت" type="date" {...field} />
          )}
        />
        <Controller
          name="reference_number"
          control={control}
          render={({ field }) => (
            <Input label="شماره مرجع" placeholder="شماره پیش‌فاکتور یا قرارداد" {...field} className="md:col-span-2" />
          )}
        />
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">یادداشت‌ها</label>
              <textarea
                {...field}
                rows={3}
                className="w-full px-4 py-2.5 rounded-xl border bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 border-neutral-300 dark:border-neutral-700"
                placeholder="یادداشت‌های مربوط به این سفارش..."
              />
            </div>
          )}
        />
      </div>

      {/* Items */}
      <div className="border-t border-neutral-200 dark:border-neutral-800 pt-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-neutral-900 dark:text-white">آیتم‌های سفارش</h4>
          <Button type="button" variant="outline" size="sm" onClick={() => appendItem({ product_id: '', variant_id: null, quantity: 1, unit_price: 0, discount_percent: 0, tax_rate: 9, notes: '' })}>
            <Plus className="w-4 h-4 ml-1" />
            افزودن آیتم
          </Button>
        </div>

        {itemFields.map((field, index) => (
          <div key={field.id} className="card p-4 mb-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-medium text-neutral-900 dark:text-white">آیتم #{index + 1}</span>
              {itemFields.length > 1 && (
                <button type="button" onClick={() => removeItem(index)} className="p-1.5 rounded-lg text-neutral-400 hover:text-danger-600 dark:hover:text-danger-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
              <Controller
                name={`items.${index}.product_id`}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select label="محصول *" options={[{ value: '', label: 'انتخاب کنید' }]} placeholder="محصول" {...field} className="md:col-span-2 lg:col-span-2" />
                )}
              />
              <Controller
                name={`items.${index}.variant_id`}
                control={control}
                render={({ field }) => (
                  <Select label="تنوع" options={[{ value: '', label: 'پیش‌فرض' }]} placeholder="تنوع" {...field} className="md:col-span-1 lg:col-span-1" />
                )}
              />
              <Controller
                name={`items.${index}.quantity`}
                control={control}
                rules={{ required: true, min: 1 }}
                render={({ field }) => (
                  <Input label="تعداد *" type="number" min={1} step={1} {...field} className="md:col-span-1 lg:col-span-1" />
                )}
              />
              <Controller
                name={`items.${index}.unit_price`}
                control={control}
                rules={{ required: true, min: 0 }}
                render={({ field }) => (
                  <Input label="قیمت واحد (ریال) *" type="number" min={0} step={100} {...field} className="md:col-span-1 lg:col-span-1" />
                )}
              />
              <Controller
                name={`items.${index}.discount_percent`}
                control={control}
                rules={{ min: 0, max: 100 }}
                render={({ field }) => (
                  <Input label="تخفیف (%)" type="number" min={0} max={100} step={0.1} {...field} className="md:col-span-1 lg:col-span-1" />
                )}
              />
              <Controller
                name={`items.${index}.tax_rate`}
                control={control}
                rules={{ min: 0, max: 100 }}
                render={({ field }) => (
                  <Input label="مالیات (%)" type="number" min={0} max={100} step={0.1} {...field} className="md:col-span-1 lg:col-span-1" defaultValue={9} />
                )}
              />
              <Controller
                name={`items.${index}.notes`}
                control={control}
                render={({ field }) => (
                  <Input label="یادداشت" placeholder="اختیاری" {...field} className="md:col-span-2 lg:col-span-6" />
                )}
              />
            </div>

            <div className="pt-3 border-t border-neutral-200 dark:border-neutral-800 flex flex-wrap gap-4 text-sm">
              <span className="font-medium text-neutral-700 dark:text-neutral-300">
                جمع: <span className="font-mono text-primary-600 dark:text-primary-400 ml-1">{formatCurrency(items[index]?.quantity * items[index]?.unit_price || 0)}</span>
              </span>
              <span className="font-medium text-neutral-700 dark:text-neutral-300">
                تخفیف: <span className="font-mono text-warning-600 dark:text-warning-400 ml-1">{formatCurrency((items[index]?.quantity * items[index]?.unit_price || 0) * (items[index]?.discount_percent || 0) / 100)}</span>
              </span>
              <span className="font-medium text-neutral-700 dark:text-neutral-300">
                مالیات: <span className="font-mono text-info-600 dark:text-info-400 ml-1">{formatCurrency((items[index]?.quantity * items[index]?.unit_price || 0) * (1 - (items[index]?.discount_percent || 0) / 100) * (items[index]?.tax_rate || 9) / 100)}</span>
              </span>
            </div>
          </div>
        ))}

        {itemFields.length === 0 && (
          <div className="text-center py-8 border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-xl">
            <Package className="w-12 h-12 text-neutral-400 mx-auto mb-2" />
            <p className="text-neutral-500 dark:text-neutral-400">هیچ آیتمی اضافه نشده است</p>
            <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => appendItem({ product_id: '', variant_id: null, quantity: 1, unit_price: 0, discount_percent: 0, tax_rate: 9, notes: '' })}>
              <Plus className="w-4 h-4 ml-1" />
              افزودن آیتم اول
            </Button>
          </div>
        )}
      </div>

      {/* Totals */}
      <div className="card p-4 bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
          <div>
            <p className="text-xs text-primary-700 dark:text-primary-300">مجموع کالا</p>
            <p className="font-bold text-neutral-900 dark:text-white font-mono">{formatCurrency(subtotal)}</p>
          </div>
          <div>
            <p className="text-xs text-primary-700 dark:text-primary-300">تخفیف کل</p>
            <p className="font-bold text-warning-600 dark:text-warning-400 font-mono">-{formatCurrency(totalDiscount)}</p>
          </div>
          <div>
            <p className="text-xs text-primary-700 dark:text-primary-300">مالیات کل</p>
            <p className="font-bold text-info-600 dark:text-info-400 font-mono">{formatCurrency(totalTax)}</p>
          </div>
          <div>
            <p className="text-xs text-primary-700 dark:text-primary-300">مبلغ خالص</p>
            <p className="font-bold text-primary-700 dark:text-primary-300 font-mono">{formatCurrency(subtotal - totalDiscount + totalTax)}</p>
          </div>
          <div className="md:col-span-2 lg:col-span-1">
            <p className="text-xs text-primary-700 dark:text-primary-300">مبلغ نهایی</p>
            <p className="text-xl font-bold text-neutral-900 dark:text-white font-mono">{formatCurrency(total)}</p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-800">
        <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>انصراف</Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? <> <Loader2 className="w-4 h-4 animate-spin" /> در حال ثبت... </> : <> <Shield className="w-4 h-4 ml-1" /> ثبت سفارش خرید </> }
        </Button>
      </div>
    </form>
  )
}

type PurchaseOrderListParams = {
  page: number
  page_size: number
  sort_by: string
  sort_order: 'asc' | 'desc'
  search?: string
  status?: string
  supplier_id?: string
  warehouse_id?: string
  date_from?: string
  date_to?: string
}